import { LargeFileUploadTask } from '@microsoft/microsoft-graph-client';
import { DriveItem } from '@microsoft/microsoft-graph-types';
import { useVariable } from '@softwareimaging/backstage';
import { FC, PropsWithChildren } from 'react';
import { Variables } from '../backstage/config';
import { UploadProvider } from './UploadProvider';
import { UploadSession, UploadStatus } from './utils';
import { useGraph } from '../sidecar/useGraphProvider';

export interface OneDriveUploadSession extends UploadSession {
  meta: {
    folder: string;
    driveId: string;
  };
  result: DriveItem;
}

interface AdditionalSessionArgs {
  folder: string;
}

export const OneDriveUploadProvider: FC<PropsWithChildren> = ({ children }) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('graphDriveId');

  return (
    <UploadProvider
      options={{
        chunkSize: 1024 * 1024 * 8, // 8MB
        chunkedUploadThreshold: 1024 * 1024 * 4 // 4MB
      }}
      createUploadSession={async (name, file, { folder }: AdditionalSessionArgs) => {
        const payload = { item: { '@microsoft.graph.conflictBehavior': 'rename', name }, deferCommit: true };
        const session = await LargeFileUploadTask.createUploadSession(
          client,
          `/drives/${driveId}/items/${folder}:/${name}:/createUploadSession`,
          payload
        );

        return {
          id: crypto.randomUUID(),
          name: name,
          size: file.size,
          url: session.url,
          created: new Date(),
          status: UploadStatus.Waiting,
          expiration: session.expiry,
          meta: { folder, driveId }
        };
      }}
      uploadFile={async (session: OneDriveUploadSession, file) => {
        await client
          .api(session.url)
          .headers({
            'Content-Length': file.size,
            'Content-Range': `bytes ${0}-${file.size - 1}/${file.size}`,
            'Content-Type': 'application/octet-stream'
          })
          .put(file);
      }}
      completeUpload={async (session: OneDriveUploadSession) => {
        return await client.api(session.url).post(undefined);
      }}
      destroyUpload={async (session: OneDriveUploadSession) => {
        await client.api(session.url).delete();
      }}
      uploadChunk={async (session: OneDriveUploadSession, chunk, { start, end }) => {
        await client
          .api(session.url)
          .headers({
            'Content-Length': end - start + 1,
            'Content-Range': `bytes ${start}-${end}/${session.size}`,
            'Content-Type': 'application/octet-stream'
          })
          .put(chunk);
      }}
    >
      {children}
    </UploadProvider>
  );
};
